<script lang="ts">
import { defineComponent, ref, watch } from 'vue';
import { faDownload, faMapMarkerAlt, faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import CButtonIcon from '@/components/shared/CButtonIcon.vue';
import MapDownloadDropdown from '@/components/map/MapDownloadDropdown.vue';
import MapViewSelector from '@/components/map/MapViewSelector.vue';
import { TourSelector } from '@/enums/TourSelector.enum';
import { MapView } from '@/enums/MapView.enum';
import CToggle from '../shared/CToggle.vue';

enum ComponentEvent {
	ZOOM_IN = 'zoomIn',
	ZOOM_OUT = 'zoomOut',
	SHOW_POPOVER = 'showPopover',
	DOWNLOAD_IMAGE = 'downloadImage',
	DOWNLOAD_PDF = 'downloadPDF',
	DOWNLOAD_FILE = 'downloadFile',
	CHANGE_VIEW = 'changeView',
  TOGGLE_CLIMATE_DATA = 'toggleClimateData'
}

/**
 *
 * @version 1.0.0
 * @since
 */
export default defineComponent({
	name: 'MapOptions',
	components: { MapDownloadDropdown, CButtonIcon, MapViewSelector, CToggle },
	props: {
		showPopover: {
			required: false,
			type: Boolean,
			default: false,
		},
		currentView: {
			required: false,
			type: String as () => MapView,
			default: MapView.NONE,
		},
    showClimateData: {
			required: false,
			type: Boolean,
			default: true,
		},
		shadowRoot: {
			required: true,
			type: [ShadowRoot, null],
		},
	},
	setup(props) {
		const isShowPopoverActive = ref<boolean>(props.showPopover);
		watch(
			() => props.showPopover,
			(value) => {
				isShowPopoverActive.value = value;
			}
		);

		return {
			TourSelector,
			ComponentEvent,
			faPlus,
			faMinus,
			faMapMarkerAlt,
			faDownload,
			isShowPopoverActive,
		};
	},
	data() {
		return {
			dropdownTriggerActive: false,
		};
	},
});
</script>

<template>
	<div class="map-options">
		<c-button-icon
			class="map-options__item"
			:data-tour="TourSelector.ZOOM_IN"
			:icon-name="faPlus.iconName"
			@click="$emit(ComponentEvent.ZOOM_IN)"
		/>
		<c-button-icon
			class="map-options__item"
			:data-tour="TourSelector.ZOOM_OUT"
			:icon-name="faMinus.iconName"
			@click="$emit(ComponentEvent.ZOOM_OUT)"
		/>
		<c-button-icon
			class="map-options__item"
			:data-tour="TourSelector.PIN"
			:icon-name="faMapMarkerAlt.iconName"
			:is-active="isShowPopoverActive"
			@click="$emit(ComponentEvent.SHOW_POPOVER)"
		/>
		<map-download-dropdown
			class="map-options__item"
			:shadow-root="shadowRoot"
			@download-image="$emit(ComponentEvent.DOWNLOAD_IMAGE)"
			@download-p-d-f="$emit(ComponentEvent.DOWNLOAD_PDF)"
			@download-file="$emit(ComponentEvent.DOWNLOAD_FILE)"
		/>
		<map-view-selector
			class="map-options__item"
			:current-view="currentView"
			:shadow-root="shadowRoot"
			@change-view="$emit(ComponentEvent.CHANGE_VIEW, $event)"
		/>
		<c-toggle
			class="map-options__item map-options__climate-toggle"
			:model-value="showClimateData"
			:label="$t('mapOptions.showClimateData')"
			@update:model-value="$emit(ComponentEvent.TOGGLE_CLIMATE_DATA, $event)"
		/>
	</div>
</template>

<style scoped lang="scss">
.map-options {
	display: flex;
	flex-direction: row;

	> .map-options__item:not(:last-child) {
		margin-right: 8px;
	}

  &__climate-toggle {
		background-color: rgba(255, 255, 255, 0.9);
		padding: 4px 8px;
		border-radius: 4px;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border: 1px solid #0000002e;
    color: #000;
	}
}
</style>
